import dayjs from 'dayjs';
import Joi from 'joi';
import type { ReactNode } from 'react';
import type { InvoiceEmailType } from 'types/invoice.types';

export type RecurringData = {
  every: number;
  unit: 'month' | 'week';
  dayToken: string;
  dayNumber?: number;
  startDate: string;
  endDate?: string | null;
  shouldSendEmail?: boolean;
  shouldSendViaPeppol?: boolean;
  emailsOptions?: InvoiceEmailType;
};

export type RecurringFormProps = {
  className?: string;
  fieldsNamesPrefix?: string;
  renderPlaceholderCols?: boolean;
  renderTemplateForm?: (
    defaultValues: RecurringData['emailsOptions'],
    onSubmit: (value: RecurringData['emailsOptions']) => void,
  ) => ReactNode;
};

export const getFieldsNames = (prefix = '') => ({
  every: prefix + 'every',
  unit: prefix + 'unit',
  dayToken: prefix + 'dayToken',
  dayNumber: prefix + 'dayNumber',
  startDate: prefix + 'startDate',
  endDate: prefix + 'endDate',
  shouldSendEmail: prefix + 'shouldSendEmail',
  shouldSendViaPeppol: prefix + 'shouldSendViaPeppol',
  emailOptions: prefix + 'emailOptions',
});

export const recurringDataFieldsNames = getFieldsNames();

export const recurringDataDefaultOptions = {
  [recurringDataFieldsNames.every]: 1,
  [recurringDataFieldsNames.unit]: 'month',
  [recurringDataFieldsNames.dayToken]: 'first',
  [recurringDataFieldsNames.shouldSendEmail]: false,
  [recurringDataFieldsNames.shouldSendViaPeppol]: false,
  [recurringDataFieldsNames.startDate]: dayjs().format('YYYY-MM-DD'),
  [recurringDataFieldsNames.endDate]: null,
} as unknown as RecurringData;

export const recurringValidationSchema = Joi.object({
  every: Joi.number().required().messages({ '*': 'validation.string.empty' }),
  unit: Joi.string().required().messages({ '*': 'validation.string.empty' }),
  dayToken: Joi.string()
    .required()
    .messages({ '*': 'validation.string.empty' }),
  dayNumber: Joi.number()
    .allow(null)
    .messages({ '*': 'validation.string.empty' }),
  startDate: Joi.string()
    .required()
    .messages({ '*': 'validation.string.empty' }),
});

const _roundDate = (
  date: string | dayjs.Dayjs,
  recurringData: Pick<RecurringData, 'unit' | 'dayToken' | 'dayNumber'>,
) => {
  const _date = dayjs(date);

  const roundedDate =
    recurringData.unit === 'week'
      ? _date
      : recurringData.dayNumber! > 28
      ? _date.set(
          'date',
          Math.min(recurringData.dayNumber!, _date.daysInMonth()),
        )
      : recurringData.dayToken === 'last'
      ? _date.endOf('month')
      : _date;

  return roundedDate.format('YYYY-MM-DD');
};

export const getFirstRecurringDate = (
  recurringData: Pick<
    RecurringData,
    'unit' | 'dayToken' | 'dayNumber' | 'startDate'
  >,
) => {
  const { unit, dayToken, dayNumber, startDate } = recurringData;
  const recurrenceStartDate = dayjs(startDate).startOf('day');

  if (unit === 'month') {
    if (dayToken === 'first') {
      const firstDayThatMonth = recurrenceStartDate
        .startOf('month')
        .format('YYYY-MM-DD');
      const startDateIsFirstDay = firstDayThatMonth === startDate;

      if (startDateIsFirstDay) {
        return firstDayThatMonth;
      }

      return recurrenceStartDate
        .add(1, 'month')
        .startOf('month')
        .format('YYYY-MM-DD');
    }

    if (dayToken === 'last') {
      return recurrenceStartDate.endOf('month').format('YYYY-MM-DD');
    }

    const startDayNumberInMonth = recurrenceStartDate.date();
    if (dayNumber! === startDayNumberInMonth) {
      return recurrenceStartDate.format('YYYY-MM-DD');
    }
    if (dayNumber! < startDayNumberInMonth) {
      const date = recurrenceStartDate.add(1, 'month');
      return date
        .set('date', Math.min(dayNumber!, date.daysInMonth()))
        .format('YYYY-MM-DD');
    }
    return recurrenceStartDate
      .set('date', Math.min(dayNumber!, recurrenceStartDate.daysInMonth()))
      .format('YYYY-MM-DD');
  }

  const startDayNumberInWeek = recurrenceStartDate.day();
  const inputDayNumber = dayNumber!;

  if (inputDayNumber < startDayNumberInWeek) {
    return recurrenceStartDate
      .set('day', inputDayNumber)
      .add(1, 'week')
      .format('YYYY-MM-DD');
  }

  if (inputDayNumber > startDayNumberInWeek) {
    return recurrenceStartDate.set('day', inputDayNumber).format('YYYY-MM-DD');
  }

  return recurrenceStartDate.format('YYYY-MM-DD');
};

export const getNextRecurringDate = (
  date: string,
  recurringData: Pick<
    RecurringData,
    'every' | 'unit' | 'dayNumber' | 'dayToken'
  >,
  options?: {
    incrementFutureEvents?: boolean;
    incrementTodayEvents?: boolean;
  },
) => {
  const incrementFutureEvents = options?.incrementFutureEvents ?? true;
  const incrementTodayEvents = options?.incrementTodayEvents ?? true;

  const firstDay = dayjs(date).startOf('day');
  const today = dayjs().startOf('day');

  const noOfEventsPassed = Math.floor(
    Math.abs(
      Math.min(
        firstDay.diff(today, recurringData.unit, true) / recurringData.every,
        0,
      ),
    ),
  );

  const offsetDate = _roundDate(
    firstDay.add(noOfEventsPassed * recurringData.every, recurringData.unit),
    recurringData,
  );

  const incrementedDate = _roundDate(
    dayjs(offsetDate).add(recurringData.every, recurringData.unit),
    recurringData,
  );

  const isFutureEvent = today.isBefore(offsetDate);
  const isTodayEvent = today.isSame(offsetDate);

  if (isFutureEvent) {
    if (!incrementFutureEvents) return offsetDate;
    return incrementedDate;
  }

  if (isTodayEvent) {
    if (!incrementTodayEvents) return offsetDate;
    return incrementedDate;
  }

  return incrementedDate;
};

export const getRecurringStepSlug = (step: number, unit: string) => {
  if (step === 1) return 'invoice.field.recurring.step_1.' + unit;
  return 'invoice.field.recurring.step';
};
